/**
 * Simplified Onboarding Preference Mapping for Te Whata Kura
 * 
 * IMPORTANT NOTE ON SPELLING:
 * ===========================
 * Practice areas use NZ spelling: "Paediatric"
 * localStorage stores US spelling: "Pediatric"
 * UI displays NZ spelling: "Paediatric"
 * 
 * This utility:
 * - Checks practice areas for "Paediatric" (NZ spelling with "ae")
 * - Returns "Pediatric" (US spelling with "e") to match localStorage
 */

import { PatientType, PracticeType } from "@context/antibioticContext";
import { practiceAreas } from "@constants/onboarding";

/**
 * Primary Care exceptions (everything else defaults to Secondary)
 */
const PRIMARY_CARE_AREAS = [
  'Community Care',
];

/**
 * Maps practice area to PracticeType
 * 
 * Rules:
 * - Anaesthetics → Surgical Prophylaxis
 * - Primary Care list → Primary
 * - Everything else → Secondary (default)
 */
export const mapPracticeAreaToPracticeType = (
  practiceArea: string | undefined
): PracticeType | null => {
  if (!practiceArea) return null;
  
  const normalized = practiceArea.trim();
  
  // Check if it's an anaesthetics specialty (Surgical Prophylaxis)
  if (normalized.includes('Anaesthetics')) {
    return 'Surgical Prophylaxis';
  }
  
  // Check if it's in the Primary Care list
  if (PRIMARY_CARE_AREAS.includes(normalized)) {
    return 'Primary';
  }
  
  // Default: Secondary (most hospital-based specialties)
  return 'Secondary';
};

/**
 * Maps practice area to PatientType
 * 
 * Rules:
 * - Contains "Paediatric" (NZ spelling in practice areas) OR "Neonatal" → "Pediatric" (US spelling in localStorage)
 * - Everything else → Adult (default)
 */
export const mapPracticeAreaToPatientType = (
  practiceArea: string | undefined
): PatientType | null => {
  if (!practiceArea) return null;
  
  const normalized = practiceArea.trim();
  
  // Check for paediatric indicators
  // Note: Practice areas use NZ spelling "Paediatric" but localStorage stores US spelling "Pediatric"
  if (normalized.includes('Paediatric') || normalized === 'Neonatal Care') {
    // Return "Pediatric" (US spelling) to match localStorage format
    return 'Pediatric' as PatientType;
  }
  
  // Default: Adult (most specialties)
  return 'Adult';
};

/**
 * Gets inferred preferences from onboarding userData
 * This is used for SUGGESTING preferences, not auto-setting them
 */
export const getInferredPreferences = (userData: {
  practiceArea?: string;
  role?: string;
  [key: string]: any;
}) => {
  const practice = mapPracticeAreaToPracticeType(userData.practiceArea);
  const patient = mapPracticeAreaToPatientType(userData.practiceArea);
  
  if (practice && patient) {
    console.log(`💡 Suggested from onboarding: ${patient} & ${practice}`, {
      practiceArea: userData.practiceArea,
    });
  }
  
  return { practice, patient };
};

/**
 * Check if user has saved preferences (manual lock)
 */
export const hasSavedPreferences = (): boolean => {
  return localStorage.getItem('whata_remember_prefs') === 'true';
};

/**
 * Clear remembered preferences (called when editing profile)
 * This will cause suggestions to show again on next use
 */
export const clearRememberedPreferences = (): void => {
  localStorage.removeItem('whata_remember_prefs');
  // Note: We keep practice_patient so usePersistState can continue working
  // It will be updated when user makes new selections
  console.log('🗑️ Cleared remembered preferences - suggestions will show on next visit');
};

/**
 * Sets default preferences in localStorage based on onboarding data
 * Only sets if preferences aren't already saved by user choice
 * 
 * IMPORTANT: Uses 'practice_patient' key to match usePersistState structure
 * 
 * @param userData - The onboarding user data object
 * @param autoEnable - Whether to automatically enable "remember preferences"
 * @returns Boolean indicating if defaults were successfully set
 */
/**
 * DEPRECATED: Use getInferredPreferences() for suggestions instead
 * This function is kept for backward compatibility but should not be used
 * 
 * The new flow is:
 * 1. Show highlighted suggestions from getInferredPreferences()
 * 2. User manually selects and clicks "Remember"
 * 3. usePersistState handles saving to localStorage
 */
export const setDefaultPreferencesFromOnboarding = (
  userData: {
    practiceArea?: string;
    role?: string;
    [key: string]: any;
  },
  autoEnable: boolean = true
): boolean => {
  console.warn('⚠️ setDefaultPreferencesFromOnboarding is deprecated - use getInferredPreferences() for suggestions');
  return false;
};

/**
 * Validation: Check if practice area exists in onboarding constants
 */
export const isValidPracticeArea = (practiceArea: string): boolean => {
  return practiceAreas.includes(practiceArea);
};

/**
 * Get all practice areas that map to a specific preference combination
 * Useful for documentation and testing
 */
export const getPracticeAreasByPreferences = (
  practice: PracticeType,
  patient: PatientType
): string[] => {
  return practiceAreas.filter(area => {
    return (
      mapPracticeAreaToPracticeType(area) === practice &&
      mapPracticeAreaToPatientType(area) === patient
    );
  });
};

/**
 * Generate complete mapping table from constants
 * This ensures documentation always matches actual behavior
 */
export const generateMappingTable = (): Record<string, { practice: PracticeType; patient: PatientType }> => {
  const mapping: Record<string, { practice: PracticeType; patient: PatientType }> = {};
  
  practiceAreas.forEach(area => {
    const practice = mapPracticeAreaToPracticeType(area);
    const patient = mapPracticeAreaToPatientType(area);
    
    if (practice && patient) {
      mapping[area] = { practice, patient };
    }
  });
  
  return mapping;
};

/**
 * For debugging: log the complete mapping
 */
export const logMappingTable = () => {
  console.table(generateMappingTable());
};

/**
 * DEBUGGING HELPER: Verify what localStorage actually stores
 * Run this in browser console after user manually saves preferences
 */
export const debugLocalStorage = () => {
  const practice = localStorage.getItem('whata_practice');
  const patient = localStorage.getItem('whata_patient');
  const rememberPrefs = localStorage.getItem('whata_remember_prefs');
  
  console.log('=== localStorage Verification ===');
  console.log('Current values:');
  console.log('  whata_practice:', practice);
  console.log('  whata_patient:', patient);
  console.log('  whata_remember_prefs:', rememberPrefs);
  console.log('');
  
  if (patient === 'Paediatric' || patient === 'Adult') {
    console.log('✅ Using TypeScript type values (Paediatric/Adult) - utility is correct!');
  } else if (patient === 'Child' || patient === 'Adult') {
    console.log('⚠️  Using display labels (Child/Adult) - update line 67 in utility to return "Child"');
  } else {
    console.log('❓ Unknown patient value format');
  }
  
  return { practice, patient, rememberPrefs };
};